/**
 * NuTopActionBar — the thin 52px glass toolbar above the content pane
 * (side-rail layout). Ported from NU-DATA-DASHBOARD/kits/_core/top-action-bar.jsx.
 *
 * Scope per brief 00 / #601 S3: the bar STRUCTURE only — page title +
 * inline-search field (the kit's fallback branch; NuInlineSearch / ⌘K command
 * palette are explicit follow-ups). The kit's product-switcher pill renders
 * only when a product registry is present, which this repo does not ship —
 * so its absence here is the kit's own behavior, not an omission. All action
 * affordances (notifications / support / profile / theme / sign-out) live in
 * the right user panel (NuUserPanel), exactly as the kit comments state.
 */

import React, { type ReactNode } from "react";

export interface NuTopActionBarProps {
  /** Page title shown at the left edge when provided. */
  title?: string;
  /**
   * [#663] Optional identity slot rendered immediately after the title.
   * Additive and backwards-compatible: omitted everywhere it is not passed,
   * so existing screens are byte-identical. The tenant lead portal uses it to
   * keep the tenant's own name/logo visible in the shell (design brief §5,
   * "tenant isolation cues") without forking the chrome.
   */
  identity?: ReactNode;
}

export function NuTopActionBar({ title, identity }: NuTopActionBarProps) {
  return (
    <header
      style={{
        height: 52,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "0 16px 0 20px",
        gap: 8,
        background: "color-mix(in srgb, var(--nu-surface) 72%, transparent)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: "1px solid var(--nu-border-strong)",
        position: "sticky",
        top: 0,
        zIndex: 25,
      }}
    >
      {title && (
        <h1
          style={{
            margin: 0,
            font: "600 16px/1 var(--nu-font-sans)",
            color: "var(--nu-text)",
            letterSpacing: ".01em",
          }}
        >
          {title}
        </h1>
      )}

      {identity}

      <span style={{ flex: 1 }} />

      {/* Inline search structure (kit fallback branch; ⌘K palette is a
          follow-up — see brief 00 out-of-scope list). */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            position: "absolute",
            top: "50%",
            left: 10,
            transform: "translateY(-50%)",
            color: "var(--nu-text-muted)",
          }}
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          placeholder="Search… ⌘K"
          aria-label="Search"
          className="nu-input"
          style={{
            height: 32,
            width: 200,
            paddingLeft: 32,
            fontSize: 12,
            background: "var(--nu-surface-alt)",
          }}
        />
      </div>
    </header>
  );
}

export default NuTopActionBar;
